// Git 数据 API(浏览器 fetch 视角)
//
// 本文件所有 JSON 端点都走 /api/v1/gitdata/... (后端 git_api.rs),由 axios
// 自动携带 Bearer token;不会触发 git CLI 专用的 Basic Auth 弹窗。
//
// 真正的 Git Smart HTTP 端点(/api/v1/git/...git/info/refs 等)只给 git CLI
// 使用,浏览器前端不应该直接请求。
import { apiClient } from './client';
import type { GitRef } from './types';

const REFS_URL = (owner: string, name: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/refs`;

const TREE_URL = (owner: string, name: string, ref: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/tree/${encodeURIComponent(ref)}`;

const BLOB_URL = (owner: string, name: string, ref: string, path: string) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/blob/${encodeURIComponent(ref)}/${path}`;

const COMMITS_URL = (owner: string, name: string, ref: string, limit: number) =>
  `/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/commits/${encodeURIComponent(ref)}?limit=${limit}`;

const enc = encodeURIComponent;

const BRANCHES_URL = (owner: string, name: string) =>
  `/v1/gitdata/${enc(owner)}/${enc(name)}/branches`;

export interface RefsResponse {
  refs: GitRef[];
}

export interface TreeEntry {
  name: string;
  path: string;
  /** "blob" / "tree" */
  kind: string;
  oid: string;
  size?: number;
  mode: string;
  /** 该路径在此 ref 下最近一次提交的标题(文件树右侧展示用),无提交时省略 */
  last_commit?: string | null;
}

export interface BlobMeta {
  oid: string;
  size: number;
  ref_name: string;
  path: string;
  text: string | null;
  mime: string;
  is_binary: boolean;
}

export interface CommitSummary {
  oid: string;
  short_oid: string;
  author_name: string;
  author_email: string;
  authored_at: string;
  committer_name: string;
  committer_email: string;
  committed_at: string;
  message: string;
  subject: string;
  parents: string[];
}

export interface TreeResponse {
  entries: TreeEntry[];
  ref_name: string;
  path: string;
}

export interface CommitsResponse {
  commits: CommitSummary[];
  ref_name: string;
  total: number;
}

export interface SchemeInfo {
  enabled: boolean;
  port: number;
}

export interface ServerInfo {
  /** 对外仓库服务器域名/IP(系统设置里配置,可空)。空时 cloneUrl 用 window.location.hostname 兜底 */
  host?: string | null;
  http: SchemeInfo;
  https: SchemeInfo;
}

export interface CreateBranchBody {
  /** 新分支名 */
  name: string;
  /** 基于哪个 ref(branch / tag / commit)创建,可选;为空则基于 HEAD */
  from?: string;
}

export interface RenameBranchBody {
  new_name: string;
}

export const gitApi = {
  /** 拉取 ref 列表(分支 + 标签),走 JSON 端点,自动携带 Bearer token */
  async listRefs(owner: string, name: string): Promise<GitRef[]> {
    const { data } = await apiClient.get<RefsResponse>(REFS_URL(owner, name));
    return data.refs;
  },

  /** 读 tree(JSON,需要 Bearer token) */
  async readTree(
    owner: string,
    name: string,
    ref: string,
  ): Promise<TreeResponse> {
    const { data } = await apiClient.get<TreeResponse>(TREE_URL(owner, name, ref));
    return data;
  },

  /** 读 blob meta(JSON) */
  async readBlobMeta(
    owner: string,
    name: string,
    ref: string,
    path: string,
  ): Promise<BlobMeta> {
    const { data } = await apiClient.get<BlobMeta>(BLOB_URL(owner, name, ref, path));
    return data;
  },

  /** 列 commit 历史(JSON) */
  async listCommits(
    owner: string,
    name: string,
    ref: string,
    limit: number = 30,
  ): Promise<CommitsResponse> {
    const { data } = await apiClient.get<CommitsResponse>(COMMITS_URL(owner, name, ref, limit));
    return data;
  },

  /** 拉取服务端协议/端口信息(前端 clone URL 切换器用) */
  async serverInfo(): Promise<ServerInfo> {
    const { data } = await apiClient.get<ServerInfo>('/v1/server/info');
    return data;
  },

  /**
   * clone URL(给用户在终端用 git CLI,需要 .git 后缀)。
   * @param scheme 'http' | 'https'
   * @param owner  仓库 owner
   * @param name   仓库名
   * @param port   对外端口(来自 serverInfo,可配置 / 已考虑 Docker-NAT 映射)
   * @param host   对外域名/IP(来自 serverInfo.host);为空时用浏览器访问到的 hostname 兜底
   *
   * host 优先取系统设置里的「对外仓库地址」,保证 clone 直连 gitman 自身端口、不经网关;
   * 未配置时才降级为当前页面 hostname。
   */
  cloneUrl(
    scheme: 'http' | 'https',
    owner: string,
    name: string,
    port: number,
    host?: string | null,
  ): string {
    const h = host?.trim() || window.location.hostname;
    return `${scheme}://${h}:${port}/api/v1/git/${encodeURIComponent(owner)}/${encodeURIComponent(name)}.git`;
  },

  /** 创建分支(POST /gitdata/.../branches) */
  async createBranch(
    owner: string,
    name: string,
    body: CreateBranchBody,
  ): Promise<GitRef> {
    const { data } = await apiClient.post<GitRef>(BRANCHES_URL(owner, name), body);
    return data;
  },

  /** 删除分支(DELETE /gitdata/.../branches/{branch}) */
  async deleteBranch(owner: string, name: string, branch: string): Promise<void> {
    await apiClient.delete(`${BRANCHES_URL(owner, name)}/${enc(branch)}`);
  },

  /** 重命名分支(PATCH /gitdata/.../branches/{branch}) */
  async renameBranch(
    owner: string,
    name: string,
    branch: string,
    newName: string,
  ): Promise<void> {
    await apiClient.patch(`${BRANCHES_URL(owner, name)}/${enc(branch)}`, {
      new_name: newName,
    });
  },
};

